/**
 * Decides when the widget speaks first.
 *
 * The launcher waits to be clicked, which means the widget only ever reaches
 * visitors who already decided they wanted it. A proactive greeting reaches the
 * ones who did not — at the cost of interrupting them, so the rules here are
 * about restraint as much as about timing:
 *
 *   • One trigger, chosen by the owner. Never several racing each other.
 *   • Once per visit. Dismissing it is remembered for the whole session, not
 *     just the page, or a five-page visit means five interruptions.
 *   • Never after the visitor engaged on their own. Someone who already opened
 *     the panel does not need to be invited into it.
 *
 * Storage is `sessionStorage` and nothing else: it is first-party to the site
 * the widget runs on, it expires with the tab, and it holds one boolean. It is
 * wrapped because sandboxed iframes and private modes throw on access rather
 * than degrade.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { WidgetSettingsPayload } from "./api-client.js";
import { logger } from "../utils/logger.js";

type ProactiveSettings = WidgetSettingsPayload["proactive"];

const STORAGE_PREFIX = "deepagent-nudge-dismissed:";

export class NudgeController implements ReactiveController {
  /** True while the bubble should be on screen. Read by the host's render. */
  visible = false;

  private readonly host: ReactiveControllerHost;
  private settings: ProactiveSettings | null = null;
  private persistKey: string | null = null;
  /** Set once the visitor engaged or refused; nothing re-arms after it. */
  private spent = false;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeningTo: "scroll" | "exit-intent" | null = null;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {}

  hostDisconnected(): void {
    this.disarm();
  }

  /**
   * Apply the owner's settings. Safe to call on every config change — the
   * builder re-applies settings on every keystroke, so this has to converge
   * rather than stack up timers.
   */
  configure(
    proactive: ProactiveSettings | undefined,
    persistKey: string | null,
  ): void {
    // Re-arm from scratch when the owner edits the trigger, so a preview shows
    // the rule they just picked instead of the one they picked a minute ago.
    const changed =
      this.settings?.enabled !== proactive?.enabled ||
      this.settings?.trigger !== proactive?.trigger ||
      this.settings?.delaySeconds !== proactive?.delaySeconds ||
      this.settings?.scrollPercent !== proactive?.scrollPercent;

    this.settings = proactive ?? null;
    this.persistKey = persistKey;
    if (changed) {
      this.disarm();
      if (this.visible) {
        this.visible = false;
        this.host.requestUpdate();
      }
      // A different rule is a different greeting, so it gets its own chance:
      // otherwise one dismissal would silence every rule the owner tries
      // afterwards, and the builder preview would go permanently blank the
      // first time they clicked the bubble.
      //
      // No effect on a live widget, where settings are fetched once and never
      // change mid-visit — `changed` is false from the second call onwards.
      // An explicit dismissal still holds: `arm()` re-checks `wasDismissed()`.
      this.spent = false;
      this.arm();
    }
  }

  /**
   * The visitor engaged, dismissed, or accepted. Any of the three ends the
   * bubble for this visit; only an explicit dismissal is worth remembering
   * across pages, since opening the panel is not a refusal.
   */
  suppress(reason: "opened" | "clicked" | "dismissed"): void {
    this.spent = true;
    this.disarm();
    if (this.visible) {
      this.visible = false;
      this.host.requestUpdate();
    }
    if (reason === "dismissed") this.rememberDismissal();
  }

  private arm(): void {
    const settings = this.settings;
    if (!settings?.enabled || this.spent || this.wasDismissed()) return;

    switch (settings.trigger) {
      case "delay":
        this.timer = setTimeout(
          () => this.show(),
          Math.max(0, settings.delaySeconds) * 1000,
        );
        break;
      case "scroll":
        this.listeningTo = "scroll";
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        break;
      case "exit-intent":
        this.listeningTo = "exit-intent";
        document.addEventListener("mouseout", this.handleExitIntent);
        break;
    }
  }

  private disarm(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.listeningTo === "scroll") {
      window.removeEventListener("scroll", this.handleScroll);
    }
    if (this.listeningTo === "exit-intent") {
      document.removeEventListener("mouseout", this.handleExitIntent);
    }
    this.listeningTo = null;
  }

  /**
   * Fires once the visitor has read the configured share of the page.
   *
   * A page shorter than the viewport has nothing to scroll and so never fires
   * this trigger. That is deliberate rather than a gap: inventing a fallback
   * would make the setting mean two different things depending on the page.
   */
  private handleScroll = (): void => {
    const settings = this.settings;
    if (!settings) return;
    const scrollable =
      document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = (window.scrollY / scrollable) * 100;
    if (percent >= settings.scrollPercent) this.show();
  };

  /**
   * Fires when the pointer leaves the top of the viewport, which in practice
   * means heading for the tab bar or the address bar. Pointer-only by nature:
   * touch devices have no equivalent gesture and simply never see it.
   */
  private handleExitIntent = (event: MouseEvent): void => {
    if (event.relatedTarget !== null) return;
    if (event.clientY > 0) return;
    this.show();
  };

  private show(): void {
    if (this.spent || this.visible) return;
    this.disarm();
    this.visible = true;
    this.host.requestUpdate();
  }

  private wasDismissed(): boolean {
    if (!this.persistKey) return false;
    try {
      return (
        window.sessionStorage.getItem(STORAGE_PREFIX + this.persistKey) === "1"
      );
    } catch (error) {
      logger.debug("nudge: sessionStorage unreadable", error);
      return false;
    }
  }

  private rememberDismissal(): void {
    if (!this.persistKey) return;
    try {
      window.sessionStorage.setItem(STORAGE_PREFIX + this.persistKey, "1");
    } catch (error) {
      logger.debug("nudge: sessionStorage unwritable", error);
    }
  }
}
